import React, { useState, useEffect } from "react";
import "./CallLists.css";
import * as ApiService from "../services/ApiService";
import { useAuth0 } from "@auth0/auth0-react";

const DoctorChip = ({
  doctor,
  className = "",
  selectable = false,
  selected = false,
  onClick = null,
  mini = false,
  disableTooltip = false,
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [doctorDetails, setDoctorDetails] = useState(null);
  const [isDeleted, setIsDeleted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { getAccessTokenSilently } = useAuth0();

  useEffect(() => {
    if (showTooltip && !disableTooltip && doctor?.staffId) {
      fetchDoctorDetails();
    }
  }, [showTooltip, doctor?.staffId, disableTooltip]);

  const fetchDoctorDetails = async () => {
    if (!doctor?.staffId) {
      setIsDeleted(true);
      setDoctorDetails(null);
      return;
    }

    setIsLoading(true);
    setDoctorDetails(null);
    try {
      const response = await ApiService.getDoctorDetails(
        getAccessTokenSilently,
        doctor.staffId
      );
      if (!response || !response.givenName) {
        setIsDeleted(true);
        setDoctorDetails(null);
        return;
      }
      setDoctorDetails(response);
      setIsDeleted(false);
    } catch (error) {
      console.error("Error fetching doctor details:", error);
      setIsDeleted(true);
      setDoctorDetails(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleMouseEnter = (e) => {
    if (disableTooltip) return;

    const rect = e.target.getBoundingClientRect();
    let scrollableParent = e.target.closest(".doctor-selection-grid");
    const scrollTop = scrollableParent ? scrollableParent.scrollTop : 0;

    setTooltipPosition({
      x: rect.left + rect.width / 2,
      y: rect.top - scrollTop,
    });
    setShowTooltip(true);
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  const handleClick = (e) => {
    if (selectable && onClick && doctor?.staffId) {
      onClick(doctor.staffId);
    }
  };

  // If doctor is undefined or null, don't render anything
  if (!doctor) {
    return null;
  }

  const chipClassName = `doctor-chip ${className} ${
    selectable ? "selectable" : ""
  } ${selected ? "selected" : ""} ${mini ? "mini" : ""}`;

  const renderTooltipContent = () => {
    if (isLoading) {
      return <p>Loading...</p>;
    }

    if (isDeleted) {
      return <p>This doctor has been deleted</p>;
    }

    if (!doctorDetails) {
      return <p>Unable to load doctor details</p>;
    }

    return (
      <>
        <h4>
          {doctorDetails.givenName} {doctorDetails.surname}
          {doctorDetails.senior && <span className="senior-badge">SENIOR</span>}
        </h4>
        <p>
          <strong>Abbreviation:</strong> {doctorDetails.abbreviation}
        </p>
        {doctorDetails.schedulingWeight !== undefined && (
          <p>
            <strong>Current Scheduling Weight:</strong>{" "}
            {typeof doctorDetails.schedulingWeight === "string"
              ? parseFloat(doctorDetails.schedulingWeight).toFixed(1)
              : Number(doctorDetails.schedulingWeight).toFixed(1)}
          </p>
        )}
        <p>
          <span
            className={`status-indicator ${
              doctorDetails.active === "Yes" ? "active" : "inactive"
            }`}
          >
            {doctorDetails.active === "Yes" ? "Active" : "Inactive"}
          </span>
        </p>
        <div className="tooltip-call-lists">
          <strong>Call Lists:</strong>
          <ul>
            {doctorDetails.CallListTypes?.length > 0 ? (
              doctorDetails.CallListTypes.map((list) => (
                <li key={list.callListTypeId}>{list.name}</li>
              ))
            ) : (
              <li>No call lists</li>
            )}
          </ul>
        </div>
      </>
    );
  };

  return (
    <>
      <span
        className={chipClassName}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
      >
        {doctor?.abbreviation || "?"}
      </span>

      {showTooltip && (
        <div
          className="doctor-tooltip"
          style={{
            position: "fixed",
            left: `${tooltipPosition.x}px`,
            top: `${tooltipPosition.y}px`,
            transform: "translate(-50%, calc(-100% - 10px))",
          }}
        >
          {renderTooltipContent()}
        </div>
      )}
    </>
  );
};

export default DoctorChip;
